import { createSlice } from "@reduxjs/toolkit";
import {
  loginUser,
  registerNewUser,
  getAuthenticatedUser,
  sendOTP,
  verifyOTP,
  resetPassword,
} from "./authAction";

interface AuthState {
  isAuthenticated: boolean;
  user: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    isActive: boolean;
    role: string;
    profileImg: string;
    mobileNo: string;
    subscription: {
      id: string;
      planName: string;
      isEmbedded?: boolean;
      isWhiteList?: boolean;
      isFleetEstimator?: boolean;
      isSiteEstimator?: boolean;
      price: number;
      quotaLimit: number;
      quotaUsed: number;
      startDate: string;
      endDate?: string;
      status: string;
      stripeCustomerId: string;
      stripeSubscriptionId: string;
      addOns: { type: string; quantity: number }[];
    } | null;
  } | null;
  loading: boolean;
  error: string | null;
  otpSent: boolean;
  otpVerified: boolean;
  passwordReset: boolean;
}

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem("token"),
  user: localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user")!)
    : null,
  loading: false,
  error: null,
  otpSent: false,
  otpVerified: false,
  passwordReset: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess(state, action) {
      state.isAuthenticated = true;
      state.user = {
        id: "",
        firstname: "",
        lastname: "",
        email: action.payload.user.email,
        isActive: true,
        role: action.payload.user.role,
        profileImg: "",
        mobileNo: "",
        subscription: null,
      };
      state.error = null;
      localStorage.setItem("user", JSON.stringify(state.user));
    },
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      state.loading = false;
      state.otpSent = false;
      state.otpVerified = false;

      // Clear all stored data
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      sessionStorage.clear();
    },
    clearAuthMessages(state) {
      state.error = null;
      state.otpSent = false;
      state.otpVerified = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // REGISTER NEW USER
      .addCase(registerNewUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerNewUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        const user = action.payload.data?.user;
        if (user) {
          state.user = {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            subscription: null, 
            profileImg: user.profileImg || "",
            mobileNo: user.mobileNo || "",
          };
        }
        state.error = null;
      })
      .addCase(registerNewUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload || "Registration failed";
      })

      // LOGIN USER
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        const user = action.payload.data?.user;
        if (user) {
          state.user = {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            subscription: null,
            profileImg: user.profileImg,
            mobileNo: user.mobileNo,
          };
        }
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.error = action.payload || "Login failed";
      })

      // GET AUTHENTICATED USER
      .addCase(getAuthenticatedUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAuthenticatedUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        const user = action.payload.data?.user;
        if (user) {
          state.user = {
            id: user.id,
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            isActive: user.isActive,
            role: user.role.rolename,
            subscription: user.subscription,
            profileImg: user.profileImg,
            mobileNo: user.mobileNo,
          };
        }
        state.error = null;
      })
      .addCase(getAuthenticatedUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || "Failed to retrieve user details";
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      })

      // SEND OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpSent = false;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.otpSent = false;
        state.error = action.payload || "Failed to send OTP";
      })

      // VERIFY OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.otpVerified = false;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpVerified = true;
        state.error = null;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.otpVerified = false;
        state.error = action.payload || "OTP verification failed";
      })

      // RESET PASSWORD
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.passwordReset = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.passwordReset = true;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.passwordReset = false;
        state.error = action.payload || "Password reset failed";
      });
  },
});

export const { loginSuccess, logout, clearAuthMessages } = authSlice.actions;
export default authSlice.reducer;
